import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { Pencil, Trash2 } from "lucide-react";
import {
  deleteSuperAdminPackage,
  getSuperAdminDashboard,
  getSuperAdminPackages,
  PaginatedResponse,
  SuperAdminDashboardData,
  SuperAdminPackage,
} from "../../services/superAdminApi";

const cardCls = "bg-white rounded-xl shadow-sm border border-gray-200 p-4";

const statusBadge = (status?: string) => {
  const active = (status || "").toLowerCase() === "active";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
        active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
};

const parsePackageModules = (modules: SuperAdminPackage["package_modules"]): string[] => {
  if (!modules) return [];
  if (Array.isArray(modules)) return modules;
  try {
    const parsed = JSON.parse(modules);
    if (Array.isArray(parsed)) return parsed.map((item) => String(item));
    if (parsed && typeof parsed === "object") {
      return Object.values(parsed)
        .filter((item) => item !== null && item !== undefined && item !== "")
        .map((item) => String(item));
    }
    return [];
  } catch {
    return [];
  }
};

const isTrueFlag = (value?: string | null) => {
  const normalized = (value || "").toLowerCase();
  return normalized === "true" || normalized === "yes";
};

const SuperAdminPackages: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuperAdminPackage[]>([]);
  const [dashboardMeta, setDashboardMeta] = useState<SuperAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginatedResponse<SuperAdminPackage> | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<SuperAdminPackage | null>(null);

  const fetchPackages = async (targetPage = page, searchOverride?: string, statusOverride?: string) => {
    const effectiveSearch = searchOverride ?? search;
    const effectiveStatus = statusOverride ?? status;

    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminPackages({
        per_page: perPage,
        page: targetPage,
        search: effectiveSearch || undefined,
        status: effectiveStatus || undefined,
      });
      setRows(res.data || []);
      setPagination(res);
      setPage(res.current_page || targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load packages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const dashboard = await getSuperAdminDashboard();
        setDashboardMeta(dashboard);
      } catch {
        // Keep list usable if summary fails.
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    fetchPackages(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const total = pagination?.total ?? rows.length;
  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const inactivePackages = useMemo(() => {
    if (!dashboardMeta) return 0;
    return Math.max(0, (dashboardMeta.packages.total || 0) - (dashboardMeta.packages.active || 0));
  }, [dashboardMeta]);

  const handleDeletePackage = async () => {
    if (!selectedPackage) return;
    try {
      await deleteSuperAdminPackage(selectedPackage.id);
      setDeleteOpen(false);
      setSelectedPackage(null);
      await fetchPackages(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to delete package.");
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Packages</h1>
        <p className="text-sm text-gray-500">Manage all subscription packages with web-style actions.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Total Packages</p>
          <p className="text-2xl font-bold text-[#402B69]">{dashboardMeta?.packages.total ?? total}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Active Packages</p>
          <p className="text-2xl font-bold text-green-600">{dashboardMeta?.packages.active ?? 0}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Inactive Packages</p>
          <p className="text-2xl font-bold text-red-600">{inactivePackages}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Package name..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          onClick={() => fetchPackages(1)}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSearch("");
            setStatus("");
            fetchPackages(1, "", "");
          }}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Reset
        </button>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate("/superadmin/packages/create")}
            className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Create New
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading packages...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Max Employees</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Max Services</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Max Deals</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Max Roles</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Package Modules</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={9}>
                        No packages found for selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => {
                      const modules = parsePackageModules(row.package_modules);
                      return (
                        <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                          <td className="px-4 py-3 text-gray-700">{(currentPage - 1) * perPage + index + 1}</td>
                          <td className="px-4 py-3 text-gray-700">
                            <div className="font-medium text-[#402B69]">
                              {row.name}
                              {isTrueFlag(row.make_private) ? (
                                <span className="ml-1 text-pink-500" title="Private">🔒</span>
                              ) : null}
                              {isTrueFlag(row.mark_recommended) ? (
                                <span className="ml-2 inline-flex rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-700">
                                  Recommended
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{row.max_employees ?? 0}</td>
                          <td className="px-4 py-3 text-gray-700">{row.max_services ?? 0}</td>
                          <td className="px-4 py-3 text-gray-700">{row.max_deals ?? 0}</td>
                          <td className="px-4 py-3 text-gray-700">{row.max_roles ?? 0}</td>
                          <td className="px-4 py-3 text-gray-700">
                            {modules.length === 0 ? (
                              <span className="text-xs text-gray-400">--</span>
                            ) : (
                              <ul className="list-disc pl-4">
                                {modules.map((moduleName, idx) => (
                                  <li key={`${row.id}-${idx}`}>{moduleName}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{statusBadge(row.status)}</td>
                          <td className="px-4 py-3 text-gray-700">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => navigate(`/superadmin/packages/${row.id}/edit`)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"
                                title="Edit"
                              >
                                <Pencil size={15} />
                              </button>
                              {row.type !== "default" ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedPackage(row);
                                    setDeleteOpen(true);
                                  }}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-700"
                                  title="Delete"
                                >
                                  <Trash2 size={15} />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing page {currentPage} of {lastPage} ({total} total packages)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchPackages(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchPackages(currentPage + 1)}
                  disabled={currentPage >= lastPage || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <p className="font-semibold mb-1">Note:</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li>Default package has special behavior and should not be deleted.</li>
          <li>Trial package behavior is controlled from Free Trial Settings.</li>
          <li>Package payment failures may impact feature access per company.</li>
        </ul>
      </div>

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeletePackage}
        title="Delete Package"
        message={`Are you sure you want to delete ${selectedPackage?.name || "this package"}?`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default SuperAdminPackages;

